/**
 * Conditional-content helpers ("Show if"). A block may carry props._condition to
 * show/hide it per-recipient based on a contact-profile attribute.
 *
 * ─── Wire format (contract for the send path) ────────────────────────────────────
 * The rule is serialised into invisible HTML-comment markers that bracket the
 * block's own HTML, in BOTH the text/html part and the text/x-amp-html part:
 *
 *   <!--xif:FIELD|OP|VALUE-->  …block html…  <!--xendif-->
 *
 *   FIELD  contact-profile attribute key, exactly as returned by
 *          GET /contacts/customer-profile-keys (e.g. `gender`, `city`, `tier`).
 *   OP     one of: is | is_not | contains | exists | not_exists | gt | lt
 *   VALUE  percent-encoded (encodeURIComponent) comparison value; EMPTY for the
 *          `exists` / `not_exists` operators, which take no value.
 *
 * ET_045: VALUE used to be base64. That is the likeliest reason the feature read as
 * "never shows for anybody" — `gender is Female` went out as
 * `<!--xif:gender|is|RmVtYWxl-->`, so a send path comparing the marker value against
 * the profile value literally could never match, and a non-matching rule hides the
 * block. Percent-encoding is still safe for the `|` and `>` delimiters but leaves
 * ordinary values (Female, Gold, Karachi) byte-for-byte identical to the profile
 * value, so a literal comparison and a decoding comparison now agree.
 *
 * The send path must decode VALUE with the equivalent of urldecode() before
 * comparing. If it does nothing with these markers at all, the content simply shows
 * for everyone — the safe default, and never a blank block.
 *
 * The rule is ALSO persisted structurally, as `props._condition` inside the saved
 * `blocks` JSON, so the send path can evaluate from that instead of parsing markers.
 */
export const CONDITION_OPS = [
  { v: 'is', label: 'is' },
  { v: 'is_not', label: 'is not' },
  { v: 'contains', label: 'contains' },
  { v: 'exists', label: 'is set' },
  { v: 'not_exists', label: 'is empty' },
  { v: 'gt', label: '>' },
  { v: 'lt', label: '<' },
];

const NO_VALUE = new Set(['exists', 'not_exists']);

export function needsValue(op) {
  return !!op && !NO_VALUE.has(op);
}

/**
 * A rule is only complete — and only emitted — once it can actually be evaluated.
 *
 * The value check matters: a half-filled rule (attribute and operator chosen, value
 * still blank) used to serialise as `<!--xif:gender|is|-->`, which a correct send
 * path evaluates as "gender equals empty string" and hides the block from everyone.
 * Treating it as no rule at all keeps the module's safe default: show the content.
 */
export function hasCondition(cond) {
  if (!cond || !cond.field || !cond.op) return false;
  if (needsValue(cond.op) && !String(cond.value || '').trim()) return false;
  return true;
}

// Wrap already-rendered block HTML in the conditional markers.
export function wrapConditional(html, cond) {
  if (!hasCondition(cond)) return html;
  const field = String(cond.field).replace(/[|>]/g, '');
  const op = cond.op;
  const val = NO_VALUE.has(op) ? '' : encodeURIComponent(String(cond.value == null ? '' : cond.value));
  return `<!--xif:${field}|${op}|${val}-->${html}<!--xendif-->`;
}

// Human-readable summary for the panel/badge.
export function describeCondition(cond) {
  if (!hasCondition(cond)) return '';
  const opLabel = (CONDITION_OPS.find(o => o.v === cond.op) || {}).label || cond.op;
  return NO_VALUE.has(cond.op)
    ? `Show if ${cond.field} ${opLabel}`
    : `Show if ${cond.field} ${opLabel} “${cond.value}”`;
}
